import { useEffect } from 'react';
import { GrabberResizer } from '@sas/react-svg';
import cx from 'classnames';
import { useResizable } from '.';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { Icon } from '../Icon';
import bundle from './NovaComponents-Resizable-gui-icu.properties';
import cn from './Resizable.css.json';
import './Resizable.css';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { useDirectSpeech } from '../DirectSpeech';
import { jsx as _jsx } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);

// Define arrow keys and their localization keys
const arrowKeys = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);
const resizeMessages = {
  ArrowLeft: 'resize.left.msg',
  ArrowRight: 'resize.right.msg',
  ArrowUp: 'resize.up.msg',
  ArrowDown: 'resize.down.msg'
};
export const Resizer = ({
  tooltip,
  ariaLabel,
  className,
  style,
  resizableElementRef,
  onResize,
  testId,
  onMouseEnter,
  onMouseLeave,
  setIsDragging
}) => {
  const isRTL = useIsRTL();
  const localize = useTextLocalizer();

  // Initialize direct speech for resizing announcements
  const resizeSuccessSpeaker = useDirectSpeech({
    mode: 'assertive'
  });
  // Initialize earcon for fallback sound
  const resizeBlockedEarcon = useEarcon({
    source: EarconName.InputFull
  });
  const {
    startResize,
    onArrowKeyResize,
    abortResize,
    isDragging
  } = useResizable({
    ref: resizableElementRef,
    onResize
  });

  // Wrap the keyDown handler to add announcements and fallback earcon
  const processArrowResize = e => {
    // Store old dimensions
    const node = resizableElementRef.current;
    const oldRect = node.getBoundingClientRect();

    // If the key is not one of the arrow keys, ignore it silently
    if (!arrowKeys.has(e.key)) {
      return;
    }

    // Trigger the resize
    onArrowKeyResize(e);

    // DOM update on the next animation frame
    requestAnimationFrame(() => {
      const newRect = node.getBoundingClientRect();

      // Compare old and new dimensions
      if (newRect.width !== oldRect.width || newRect.height !== oldRect.height) {
        // Size changed – announce
        const messageKey = resizeMessages[e.key];
        const announcement = localize(messageKey);
        resizeSuccessSpeaker?.notify(announcement);
      } else {
        // Otherwise play earcon
        resizeBlockedEarcon.play();
      }
    });
  };
  useEffect(() => {
    setIsDragging?.(isDragging);
  }, [isDragging, setIsDragging]);
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars({
    'color-text-label': dt.tokens['color-label-text'],
    'focus-outline': 'calc(-1 * var(--focus-width))'
  }, 'resizer');
  return /*#__PURE__*/_jsx("div", {
    role: "img",
    tabIndex: 0,
    className: cx(cn.gripIconWrapper, isRTL && cn.gripCursorRtl, className),
    style: style,
    onMouseDown: startResize,
    onKeyDown: processArrowResize // Use wrapped keyDown handler for resizing announcements
    ,
    onMouseUp: abortResize,
    "data-testid": testId,
    title: tooltip ?? localize('gripIcon.label'),
    "aria-label": ariaLabel ?? localize('gripIcon.aria'),
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    children: /*#__PURE__*/_jsx(Icon, {
      icon: GrabberResizer,
      tooltip: "",
      ariaLabel: "",
      style: tokens
    })
  });
};
//# sourceMappingURL=Resizer.js.map